import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Heartbeat, CaretDown, CaretRight, Plus, Trash } from "@phosphor-icons/react";

const WEEKS_KEY = "bsvac_health_weeks";
const STATUS = {
  problem: { label: "Needs attention", cls: "bg-red-600 text-white" },
  unpublished: { label: "Complete · not published", cls: "bg-amber-400 text-amber-950" },
  empty: { label: "No shifts loaded", cls: "bg-slate-400 text-white" },
  ok: { label: "Healthy", cls: "bg-emerald-600 text-white" },
};

const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const savedWeeks = () => Math.min(52, Math.max(1, parseInt(localStorage.getItem(WEEKS_KEY) || "12", 10) || 12));

export function useScheduleHealth(enabled) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const refresh = useCallback(async (weeks = savedWeeks()) => {
    if (!enabled) return;
    try {
      const { data: d } = await api.get(`/schedule-health/?weeks=${weeks}`, { timeout: 120000 });
      setData(d); setError(null);
    } catch (e) { setData(null); setError(e.response?.data?.detail || "Could not load schedule health"); }
  }, [enabled]);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, error, refresh };
}

export function ScheduleHealthButton({ health, onClick }) {
  const problems = health.data?.problem_weeks || 0;
  const needsSetup = !health.data && health.error;
  return (
    <Button variant="outline" onClick={onClick} className={`rounded-sm h-9 text-xs relative ${problems ? "border-red-600 text-red-800 hover:bg-red-50" : ""}`} data-testid="btn-schedule-health">
      <Heartbeat size={14} weight="bold" className="mr-1" />SCHEDULE HEALTH
      {problems > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold" data-testid="schedule-health-badge">{problems}</span>}
      {needsSetup && <span className="ml-2 px-1 rounded-sm bg-amber-400 text-amber-950 text-[9px] font-bold" data-testid="schedule-health-setup-badge">SET UP</span>}
    </Button>
  );
}

function IssueList({ title, items, testid, extra }) {
  if (!items.length) return null;
  return (
    <div className="mt-2" data-testid={testid}>
      <div className="tab-tag text-slate-500 mb-1">{title}</div>
      <div className="max-h-40 overflow-y-auto space-y-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between bg-white border border-slate-200 rounded-sm px-2 py-1 text-xs">
            <span><strong>{it.day} {fmtDate(it.date).replace(/, \d{4}$/, "")}</strong> <span className="mono">{it.start}–{it.end}</span> · {it.position}{it.ambulance_role ? ` · ${it.ambulance_role}` : ""}</span>
            <span className="mono text-slate-600">×{it.count}{extra && it.removable < it.count ? ` (${it.count - it.removable} kept: assigned/published)` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WeekRow({ w, onFix, busy }) {
  const [open, setOpen] = useState(w.status === "problem");
  const st = STATUS[w.status];
  const hasDetails = w.missing.length > 0 || w.extra.length > 0;
  return (
    <div className="border border-slate-200 rounded-sm bg-slate-50" data-testid={`health-week-${w.week_start}`}>
      <div className="flex items-center gap-2 px-2 py-2 flex-wrap">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-sm font-semibold" disabled={!hasDetails} data-testid={`health-week-toggle-${w.week_start}`}>
          {hasDetails ? (open ? <CaretDown size={12} /> : <CaretRight size={12} />) : <span className="w-3" />}
          <span className="mono">{fmtDate(w.week_start)} – {fmtDate(w.week_end)}</span>
        </button>
        <Badge className={`rounded-sm text-[10px] ${st.cls}`} data-testid={`health-week-status-${w.week_start}`}>{st.label}</Badge>
        <span className="text-xs text-slate-600 mono" data-testid={`health-week-counts-${w.week_start}`}>
          {w.actual}/{w.expected} shifts · {w.missing_count} missing · {w.extra_count} extra · {w.draft_count} draft · {w.published_count} published
        </span>
        <div className="ml-auto flex gap-1">
          {w.missing_count > 0 && <Button size="sm" variant="outline" disabled={busy} onClick={() => onFix(w, "add")} className="rounded-sm h-7 text-[11px]" data-testid={`health-add-missing-${w.week_start}`}><Plus size={12} className="mr-1" />Add missing ({w.missing_count})</Button>}
          {w.removable_extra_count > 0 && <Button size="sm" variant="outline" disabled={busy} onClick={() => onFix(w, "remove")} className="rounded-sm h-7 text-[11px] border-red-600 text-red-800 hover:bg-red-50" data-testid={`health-remove-extra-${w.week_start}`}><Trash size={12} className="mr-1" />Remove extra drafts ({w.removable_extra_count})</Button>}
        </div>
      </div>
      {open && hasDetails && (
        <div className="px-3 pb-2">
          <IssueList title="Missing vs. baseline" items={w.missing} testid={`health-missing-list-${w.week_start}`} />
          <IssueList title="Extra (duplicates or not in baseline)" items={w.extra} testid={`health-extra-list-${w.week_start}`} extra />
        </div>
      )}
      {w.status === "problem" && w.draft_count > 0 && w.published_count > 0 && (
        <div className="px-3 pb-2 text-xs text-red-800" data-testid={`health-mixed-${w.week_start}`}>Partly published: {w.draft_count} shift(s) are still drafts and hidden from volunteers. Open this week and use Publish Week.</div>
      )}
    </div>
  );
}

function BaselinePicker({ value, onChange }) {
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/templates/").then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white" data-testid="health-baseline-select">
      <option value="" disabled>— Choose the baseline template —</option>
      {templates.map((t) => <option key={t.id} value={t.id}>{`${t.name} · ${t.slots.length} slot(s)`}</option>)}
    </select>
  );
}

function FixConfirm({ pending, onCancel, onConfirm }) {
  if (!pending) return null;
  const { w, kind } = pending;
  const add = kind === "add";
  return (
    <AlertDialog open onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="health-fix-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{add ? `Add ${w.missing_count} missing shift(s)?` : `Remove ${w.removable_extra_count} extra draft shift(s)?`}</AlertDialogTitle>
          <AlertDialogDescription>
            Week of {fmtDate(w.week_start)}. {add
              ? `New shifts are created ${w.published_count > w.draft_count ? "as PUBLISHED (this week is published)" : "as drafts"}. Existing shifts are not changed.`
              : "Only unassigned draft duplicates are removed. Assigned or published shifts are never touched."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="health-fix-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className={add ? "bg-slate-900" : "bg-red-700 hover:bg-red-800"} data-testid="health-fix-confirm-btn">{add ? "Add missing" : "Remove extras"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ScheduleHealthDialog({ open, onOpenChange, health, onChanged }) {
  const [baselineId, setBaselineId] = useState(null);
  const [weeks, setWeeks] = useState(savedWeeks());
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/schedule-health/config").then((r) => setBaselineId(r.data.baseline_template_id)).catch(() => {});
    health.refresh(savedWeeks());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function chooseBaseline(id) {
    try {
      await api.put("/schedule-health/config", { baseline_template_id: id });
      setBaselineId(id); toast.success("Baseline template saved");
      health.refresh(weeks);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save baseline"); }
  }

  function applyWeeks(v) {
    const n = Math.min(52, Math.max(1, parseInt(v, 10) || 12));
    setWeeks(n); localStorage.setItem(WEEKS_KEY, String(n)); health.refresh(n);
  }

  async function runFix() {
    const { w, kind } = pending;
    setPending(null); setBusy(true);
    try {
      const { data } = await api.post(`/schedule-health/weeks/${w.week_start}/${kind === "add" ? "add-missing" : "remove-extra"}`, {}, { timeout: 120000 });
      toast.success(kind === "add" ? `Added ${data.created} shift(s) to week of ${fmtDate(w.week_start)}` : `Removed ${data.deleted} extra draft(s) from week of ${fmtDate(w.week_start)}`);
      await health.refresh(weeks); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Fix failed"); }
    finally { setBusy(false); }
  }

  const list = health.data?.weeks || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="schedule-health-dialog">
        <DialogHeader><DialogTitle>Schedule Health</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Compares each upcoming week against the baseline template and flags missing, extra and unpublished shifts.</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px] gap-3 mt-2">
          <div><Label className="tab-tag">Baseline template</Label><BaselinePicker value={baselineId} onChange={chooseBaseline} /></div>
          <div><Label className="tab-tag">Weeks ahead (1–52)</Label><Input type="number" min="1" max="52" defaultValue={weeks} onBlur={(e) => applyWeeks(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyWeeks(e.target.value)} className="rounded-sm mono" data-testid="health-weeks-input" /></div>
        </div>
        {health.error && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-2" data-testid="health-error">{health.error}</div>}
        {health.data && (
          <div className="text-sm font-semibold mt-2" data-testid="health-summary">
            {health.data.problem_weeks ? `${health.data.problem_weeks} of ${list.length} week(s) need attention` : `No problems found in the next ${list.length} week(s)`}
          </div>
        )}
        <div className="space-y-2 mt-2">{list.map((w) => <WeekRow key={w.week_start} w={w} busy={busy} onFix={(wk, kind) => setPending({ w: wk, kind })} />)}</div>
        <FixConfirm pending={pending} onCancel={() => setPending(null)} onConfirm={runFix} />
      </DialogContent>
    </Dialog>
  );
}
